import React from 'react';
import { withRouter } from 'react-router-dom';

// material components
import NotificationsActiveIcon from '@material-ui/icons/NotificationsActive';
import StyledBadge from '@material-ui/core/Badge';
import IconButton from '@material-ui/core/IconButton';

// custom components
import AlertsDrawer from './AlertsDrawer';
import withShipment from '../../withShipment';

// constants
import {
  POINT_NO_CLEAR_SUBJECT,
  SHOW_NO_CLEAR_IMAGE_TAB,
  canSeeAccessRequests,
} from './constants';

// redux
import { alertEntriesSelector } from '../../redux/selectors/alerts';
import { userInfoSelector } from '../../redux/selectors/auth';
import { refreshUserInfo } from '../../redux/actions/auth';
import {
  fetchAlerts,
  snoozeAlert,
  resolveAlert,
  deleteAllAlerts,
} from '../../redux/actions/alerts';
import PropTypes from 'prop-types';

import { tableSettingSelector } from '../../redux/selectors/account';
import { getTableSetting } from '../../redux/actions/account';
import { Tooltip } from '@mui/material';

// Cross-company access requests share the bell: the alert row is the
// nudge, the Access Requests tab is where they are acted on.
import {
  fetchAccessRequests,
  decideAccessRequest,
} from '../../redux/actions/accessRequests';
import {
  incomingAccessRequestsSelector,
  incomingDecidedAccessSelector,
  outgoingAccessRequestsSelector,
  accessRequestBadgeCountSelector,
} from '../../redux/selectors/accessRequests';

// styling

class Alerts extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      alertDrawerOpen: false,
      alerts: [],
    };
  }

  handleAlertDrawerClose = () => {
    this.setState({
      alertDrawerOpen: false,
    });
  };

  parseAlertTimestamp = (ts) => {
    if (!ts) return 0;
    // Format: "MM\dd\yyyy h:mm a" or "MM/dd/yyyy h:mm a"
    const parts = ts.trim().split(/\s+/);
    const datePart = parts[0];
    const timePart = parts[1] || '00:00';
    const meridiem = (parts[2] || 'AM').toUpperCase();

    const [mm, dd, yyyy] = datePart.split(/[/\\]/);
    let [h, min] = timePart.split(':').map(Number);
    if (meridiem === 'PM' && h < 12) h += 12;
    if (meridiem === 'AM' && h === 12) h = 0;

    return new Date(
      parseInt(yyyy),
      parseInt(mm) - 1,
      parseInt(dd),
      h,
      min
    ).getTime();
  };

  // Parse once per alert, not twice per comparison. The comparator used
  // to call parseAlertTimestamp on both sides of every comparison, so a
  // list of n cost ~2n*log(n) date constructions on the main thread every
  // poll tick. Decorate-sort-undecorate makes it n.
  sortAlertsByLatest = (alerts) => {
    return [...alerts]
      .map((alert) => ({
        alert,
        at: this.parseAlertTimestamp(alert.timestamp),
      }))
      .sort((a, b) => b.at - a.at)
      .map((entry) => entry.alert);
  };

  // Poll fallback so ANY alert (from any backend path, not just the
  // WebSocket-pushed ones) shows up without a page reload.
  ALERT_POLL_MS = 30000;

  componentDidMount() {
    this.props.getTableSetting({});

    this.refreshAlerts();

    this.setState({ alerts: this.sortAlertsByLatest(this.props.alertEntries) });

    this.alertPoll = setInterval(this.refreshAlerts, this.ALERT_POLL_MS);
  }

  componentWillUnmount() {
    if (this.alertPoll) clearInterval(this.alertPoll);
  }

  // An access decision made by ANOTHER company's owner changes this
  // user's own location list server-side. Their session cannot know
  // that on its own, so the decision alert — which lands at their home
  // company and is already being polled for — is used as the signal to
  // re-read the account. Without it a granted location stays invisible
  // until the next login: the access is real, the picker just never
  // hears about it.
  ACCESS_DECISION_TYPE = 'ACCESS_REQUEST_DECISION';

  refreshOnAccessDecision = (alerts) => {
    const latest = (alerts || []).find(
      (a) => a?.type === this.ACCESS_DECISION_TYPE
    );
    if (!latest || latest.alertId === this.lastSeenDecisionAlertId) return;
    this.lastSeenDecisionAlertId = latest.alertId;
    this.props.refreshUserInfo();
  };

  refreshAlerts = () => {
    const locationId = this.props.userInfo?.selectedLocationId;
    if (!locationId) return;
    this.props.fetchAlerts({ locationId });
    // Ride the same poll rather than opening a second timer or a socket:
    // an access request is not time-critical to the second.
    //
    // Owner tier ONLY. /accessRequest/* is @Secured to the owner tier and
    // answers everyone else with 403 - which AuthUtil reads as a dead
    // session and logs out. An employee or supervisor was therefore being
    // signed out every poll tick. Their queue is empty by definition, so
    // there is nothing to fetch: don't ask.
    if (canSeeAccessRequests(this.props.userInfo)) {
      this.props.fetchAccessRequests({
        locationId,
        companyId: this.props.userInfo?.companyId,
      });
    }
  };

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (
      prevProps.alertEntries !== this.props.alertEntries ||
      prevProps.account !== this.props.account
    ) {
      this.removeSnoozedAlerts();
      this.refreshOnAccessDecision(this.props.alertEntries);
    }
  }

  removeSnoozedAlerts = () => {
    // account is the userTableSetting blob — undefined until
    // getTableSetting resolves, OR when the user has never saved one.
    // Optional-chain the whole path so a re-fetch (e.g. after delete)
    // doesn't crash before account hydrates.
    const snoozedAlertIds = (this.props.account?.snoozedAlerts || []).map(
      (alert) => alert.alertId
    );
    const unsnoozedAlerts = (this.props.alertEntries || []).filter(
      (alert) => !snoozedAlertIds.includes(alert.alertId)
    );

    this.setState({ alerts: this.sortAlertsByLatest(unsnoozedAlerts) });
  };

  // While the "No Clear Image" tab is hidden the badge must count global alerts
  // only — otherwise it advertises entries the user can't reach anywhere. When
  // the flag is flipped back on, both kinds are reachable so both are counted.
  getBadgeCount = () => {
    const alertCount = SHOW_NO_CLEAR_IMAGE_TAB
      ? this.state.alerts.length
      : this.state.alerts.filter(
          (alert) => alert?.type !== POINT_NO_CLEAR_SUBJECT
        ).length;
    // Pending access requests are counted too — they are the one thing in
    // this drawer that BLOCKS someone else's work until it is answered.
    const accessCount = canSeeAccessRequests(this.props.userInfo)
      ? this.props.accessRequestCount || 0
      : 0;
    return alertCount + accessCount;
  };

  toggleAlertDrawer = () => {
    this.setState({
      alertDrawerOpen: true,
    });
  };

  // "No clear subject" alert clicked — close the drawer and jump to Points
  // Watching (By session) for that date, with the giving employee filtered
  // and the "Unclear photos" chip pre-selected so the flagged entry is in view.
  navigateToPointWatching = (identifier) => {
    if (!identifier || !identifier.date) return;
    this.handleAlertDrawerClose();
    this.props.history.push('/point-watching', {
      pwDate: identifier.date,
      pwUserId: identifier.userId,
      pwUnclear: true,
    });
  };
  handleResolveAlert = (alertId, index) => {
    let data = {
      alertId: alertId,
      shippingTemplateChangedIndex: index,
      locationId: this.props.userInfo.selectedLocationId,
    };
    this.props.resolveAlert(data);
  };

  render() {
    return (
      <>
        <Tooltip title="Notifications">
          <IconButton
            style={{ marginRight: '4px' }}
            aria-label="alerts"
            onClick={this.toggleAlertDrawer}
          >
            <StyledBadge
              badgeContent={this.getBadgeCount()}
              color="error"
              overlap="rectangular"
            >
              <NotificationsActiveIcon
                color="secondary"
                style={{ fontSize: '30px' }}
              />
            </StyledBadge>
          </IconButton>
        </Tooltip>

        <AlertsDrawer
          alertDrawerOpen={this.state.alertDrawerOpen}
          handleAlertDrawerClose={this.handleAlertDrawerClose}
          alertEntries={this.state.alerts}
          noAlerts={this.state.alerts.length === 0}
          closeDrawer={this.handleAlertDrawerClose}
          snoozeAlert={this.props.snoozeAlert}
          resolveAlert={this.handleResolveAlert}
          removeSnoozedAlerts={this.removeSnoozedAlerts}
          user={this.props.userInfo}
          deleteAllAlert={this.props.deleteAllAlerts}
          onAlertNavigate={this.navigateToPointWatching}
          incomingRequests={this.props.incomingRequests}
          incomingDecided={this.props.incomingDecided}
          outgoingRequests={this.props.outgoingRequests}
          decideAccessRequest={this.props.decideAccessRequest}
        />
      </>
    );
  }
}

const mapStateToProps = (state) => ({
  alertEntries: alertEntriesSelector(state),
  userInfo: userInfoSelector(state),
  account: tableSettingSelector(state),
  incomingRequests: incomingAccessRequestsSelector(state),
  incomingDecided: incomingDecidedAccessSelector(state),
  outgoingRequests: outgoingAccessRequestsSelector(state),
  accessRequestCount: accessRequestBadgeCountSelector(state),
});

const actionCreators = {
  fetchAlerts,
  snoozeAlert,
  resolveAlert,
  getTableSetting,
  deleteAllAlerts,
  fetchAccessRequests,
  decideAccessRequest,
  refreshUserInfo,
};

Alerts.propTypes = {
  alertEntries: PropTypes.array,
  userInfo: PropTypes.object,
  account: PropTypes.object,
};

Alerts.defaultProps = {
  alertEntries: [],
  userInfo: {},
  account: {},
};

export default withRouter(
  withShipment(
    {
      mapStateToProps,
      actionCreators,
    },
    Alerts
  )
);
